// Toast / inline confirmation system. Replaces window.alert / confirm /
// prompt globally. Two surfaces:
//
//   1. <ToastProvider> wraps the app, exposes useToast() hook returning
//      `{ show(message, opts), confirm(message, opts) }`.
//   2. Toasts stack bottom-right, auto-dismiss after 4s (success/info) or
//      manual-dismiss (error). One render path, consistent visual.
//
// confirm() returns Promise<boolean>; type-to-confirm variant available
// via opts.requireType (e.g. requireType: 'archive') so destructive ops
// can't be one-clicked away.

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface ToastEntry {
  id: number;
  kind: 'success' | 'info' | 'error' | 'warning';
  message: React.ReactNode;
  ttlMs: number | null;
}

interface ConfirmOpts {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  requireType?: string; // operator must type this string to enable confirm
}

interface ToastApi {
  show: (message: React.ReactNode, opts?: { kind?: ToastEntry['kind']; ttlMs?: number | null }) => void;
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
}

const ToastCtx = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be inside <ToastProvider>');
  return ctx;
}

let nextId = 1;

export function ToastProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const [pendingConfirm, setPendingConfirm] = useState<{ opts: ConfirmOpts; resolve: (ok: boolean) => void } | null>(null);

  const show = useCallback<ToastApi['show']>((message, opts = {}) => {
    const id = nextId++;
    const kind = opts.kind ?? 'info';
    const ttlMs = opts.ttlMs ?? (kind === 'error' ? null : 4000);
    setToasts(prev => [...prev, { id, kind, message, ttlMs }]);
    if (ttlMs !== null) {
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), ttlMs);
    }
  }, []);

  const confirm = useCallback<ToastApi['confirm']>((opts) => {
    return new Promise<boolean>(resolve => setPendingConfirm({ opts, resolve }));
  }, []);

  // Stable context value — without useMemo, every render of the
  // provider (caused by adding/removing a toast) creates a fresh
  // `{ show, confirm }` object reference. Every consumer that lists
  // `toast` in a useEffect dep array would re-fire that effect on
  // every toast notification — most notably DataSurfaceV3's preview
  // re-fetch, which is the cause of "auto-refresh kills my work":
  // each new-arrival toast triggered another fetch, which set
  // previewRows, which re-rendered the table, which re-anchored
  // hover popovers, etc. show + confirm are useCallback'd with []
  // deps, so this memo is stable across the provider's lifetime.
  const value = useMemo<ToastApi>(() => ({ show, confirm }), [show, confirm]);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      {/* Stacked toasts. role+aria-live on the live region so screen
          readers announce success/info messages without the operator
          having to navigate to the toast — and assertive on errors so
          they break through other announcements. */}
      {toasts.length > 0 && (
        <div
          role="region"
          aria-label="Notifications"
          style={{
            position: 'fixed', bottom: 16, right: 16, zIndex: 1200,
            display: 'flex', flexDirection: 'column', gap: 8,
            maxWidth: 'min(420px, calc(100vw - 32px))',
          }}
        >
          {toasts.map(t => (
            <div
              key={t.id}
              role={t.kind === 'error' ? 'alert' : 'status'}
              aria-live={t.kind === 'error' ? 'assertive' : 'polite'}
              style={{
              padding: '10px 14px', borderRadius: 6,
              fontSize: 13, fontFamily: 'var(--ui)',
              background: t.kind === 'error' ? 'oklch(0.95 0.06 25)'
                : t.kind === 'warning' ? 'oklch(0.95 0.06 75)'
                : t.kind === 'success' ? 'var(--accent-soft)'
                : 'var(--bg-elev)',
              border: `1px solid ${t.kind === 'error' ? 'var(--danger)' : t.kind === 'warning' ? 'oklch(0.78 0.10 75)' : t.kind === 'success' ? 'var(--accent-line)' : 'var(--line)'}`,
              color: 'var(--ink)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              animation: 'toast-in 200ms ease-out',
            }}>
              <span style={{
                fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase',
                letterSpacing: '0.08em', color: 'var(--ink-3)',
                marginTop: 2, minWidth: 56,
              }}>{t.kind}</span>
              <div style={{ flex: 1, lineHeight: 1.45 }}>{t.message}</div>
              <button
                onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
                style={{ background: 'transparent', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', padding: 0, fontSize: 16, lineHeight: 1 }}
              >×</button>
            </div>
          ))}
        </div>
      )}
      {/* Modal confirm */}
      {pendingConfirm && (
        <ConfirmModal
          opts={pendingConfirm.opts}
          onResolve={(ok) => { pendingConfirm.resolve(ok); setPendingConfirm(null); }}
        />
      )}
      <style>{`
        @keyframes toast-in {
          from { transform: translateX(20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </ToastCtx.Provider>
  );
}

function ConfirmModal({ opts, onResolve }: { opts: ConfirmOpts; onResolve: (ok: boolean) => void }): React.ReactElement {
  const [typed, setTyped] = useState('');
  const canConfirm = !opts.requireType || typed === opts.requireType;
  return (
    <div onClick={() => onResolve(false)} style={{
      position: 'fixed', inset: 0, zIndex: 1300,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 'min(440px, 100%)', background: 'var(--bg)',
        border: '1px solid var(--line)', borderRadius: 12, padding: 22,
        boxShadow: '0 16px 48px rgba(0,0,0,0.32)',
      }}>
        {opts.title && <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'var(--ink)' }}>{opts.title}</div>}
        <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 14, whiteSpace: 'pre-wrap' }}>{opts.message}</div>
        {opts.requireType && (
          <>
            <div style={{ fontSize: 11.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)', marginBottom: 4 }}>
              Type <code style={{ background: 'var(--bg-sunken)', padding: '1px 4px', borderRadius: 3 }}>{opts.requireType}</code> to confirm:
            </div>
            <input
              autoFocus value={typed} onChange={e => setTyped(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && canConfirm) onResolve(true); else if (e.key === 'Escape') onResolve(false); }}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 10px', fontSize: 13, fontFamily: 'var(--mono)',
                background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 4,
                outline: 'none', color: 'var(--ink)', marginBottom: 14,
              }}
            />
          </>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={() => onResolve(false)}>{opts.cancelLabel ?? 'Cancel'}</button>
          <button
            className={opts.destructive ? 'btn' : 'btn primary'}
            onClick={() => canConfirm && onResolve(true)}
            disabled={!canConfirm}
            style={opts.destructive ? { background: 'var(--danger)', color: '#fff', border: 'none' } : undefined}
          >{opts.confirmLabel ?? 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}
